import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, CardTitle } from 'reactstrap';
import { trimEnd } from 'lodash';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import {
  findQuestionById,
  findQuestionByKeys,
} from 'js/utils/assessment-cohorts';
import { positionBetween } from 'js/utils/big';
import BreakoutListItem from './BreakoutListItem';
import { breakoutPathSegment } from './breakout-utils';
import styles from './AssessmentReviewDetailBreakouts.module.scss';

function normalizeBreakouts(breakouts = []) {
  return breakouts.map((b) => ({
    id: b.id,
    name: b.name,
    question_ids: b.question_ids || [],
    question_set_key: b.question_set_key || '',
    question_key: b.question_key || '',
    grouping_mode: b.grouping_mode || 'each_answer',
    groups: b.groups || {},
    position: b.position != null ? String(b.position) : undefined,
  }));
}

function reorderList(list, startIndex, finishIndex) {
  const next = [...list];
  const [removed] = next.splice(startIndex, 1);
  next.splice(finishIndex, 0, removed);
  return next;
}

function destinationIndex(startIndex, indexOfTarget, closestEdgeOfTarget) {
  if (closestEdgeOfTarget === 'top') {
    return startIndex < indexOfTarget ? indexOfTarget - 1 : indexOfTarget;
  }
  return startIndex < indexOfTarget ? indexOfTarget : indexOfTarget + 1;
}

function positionForMove(list, index) {
  const before = list[index - 1];
  const after = list[index + 1];
  return positionBetween(before?.position ?? 0, after?.position ?? 1);
}

const AssessmentReviewDetailBreakouts = ({ assessment, onSave, basePath }) => {
  const match = useRouteMatch();
  const listBasePath = trimEnd(basePath || match.url, '/');

  const serverBreakouts = useMemo(
    () => normalizeBreakouts(assessment?.breakouts),
    [assessment?.breakouts]
  );

  const [items, setItems] = useState(serverBreakouts);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const breakoutsKey = JSON.stringify(assessment?.breakouts ?? []);
  useEffect(() => {
    setItems(normalizeBreakouts(assessment?.breakouts));
  }, [breakoutsKey, assessment?.breakouts]);

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  const persist = useCallback(
    (next, extra = {}) => {
      setItems(next);
      return onSave?.(next, { silent: true, ...extra });
    },
    [onSave]
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data?.type === 'breakout-item',
      onDrop: ({ source, location }) => {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }
        const startIndex = source.data.index;
        const indexOfTarget = target.data.index;
        const closestEdgeOfTarget = extractClosestEdge(target.data);
        if (
          typeof startIndex !== 'number' ||
          typeof indexOfTarget !== 'number'
        ) {
          return;
        }

        const finishIndex = destinationIndex(
          startIndex,
          indexOfTarget,
          closestEdgeOfTarget
        );

        if (finishIndex === startIndex) {
          return;
        }

        const next = reorderList(itemsRef.current, startIndex, finishIndex);
        const moved = next[finishIndex];
        const position = positionForMove(next, finishIndex);
        next[finishIndex] = { ...moved, position };
        persist(next, { reorder: true, movedId: moved.id, position });
      },
    });
  }, [persist]);

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteIndex == null) {
      return Promise.resolve();
    }
    const deletedId = itemsRef.current[pendingDeleteIndex]?.id;
    const next = itemsRef.current.filter((_, i) => i !== pendingDeleteIndex);
    setPendingDeleteIndex(null);
    return persist(next, { deletedId });
  }, [pendingDeleteIndex, persist]);

  const pendingDelete =
    pendingDeleteIndex == null ? null : items[pendingDeleteIndex];

  if (!assessment) {
    return null;
  }

  return (
    <div className={`content survey-review-detail ${styles.root}`}>
      <Card tag="section">
        <CardHeader>
          <div className="action-items">
            <Button
              tag={Link}
              to={`${listBasePath}/new`}
              color="primary"
              outline
            >
              Create breakout
            </Button>
          </div>
          <CardTitle tag="h5">Breakouts</CardTitle>
          <p>Group participants by their answers to questions</p>
        </CardHeader>
        <CardBody className={styles.listBody}>
          {items.length === 0 ? (
            <p className="text-muted mb-0">
              No breakouts yet. Create one to slice results by a question.
            </p>
          ) : (
            <ul className={styles.list}>
              {items.map((breakout, index) => {
                const question =
                  findQuestionByKeys(
                    assessment,
                    breakout.question_set_key,
                    breakout.question_key
                  ) || findQuestionById(assessment, breakout.question_ids?.[0]);
                return (
                  <BreakoutListItem
                    key={breakout.id || `${breakout.name}-${index}`}
                    index={index}
                    breakout={breakout}
                    question={question}
                    to={`${listBasePath}/${breakoutPathSegment(breakout.name)}`}
                    onRequestDelete={() => setPendingDeleteIndex(index)}
                  />
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <ConfirmModal
        isOpen={pendingDeleteIndex != null}
        title="Delete breakout?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteIndex(null)}
        confirmButtonLabel="Delete"
        confirnButtonColor="danger"
      >
        <p>
          Remove breakout <strong>{pendingDelete?.name || 'Untitled'}</strong>?
          This cannot be undone.
        </p>
      </ConfirmModal>
    </div>
  );
};

export default AssessmentReviewDetailBreakouts;
